/**
 * scriptLinter — local pre-flight check for TTS scripts.
 * No network calls. Catches common issues before ElevenLabs credits are spent.
 */

// Words that commonly follow "its" as a possessive (not "it's")
const POSSESSIVE_NOUNS = new Set([
  'magic', 'color', 'colours', 'name', 'way', 'own', 'best', 'full', 'new',
  'original', 'place', 'purpose', 'power', 'potential', 'price', 'peak',
  'beauty', 'charm', 'core', 'design', 'edge', 'effect', 'features',
  'finest', 'first', 'form', 'glory', 'heart', 'height', 'impact',
  'job', 'kind', 'limit', 'look', 'mark', 'max', 'most', 'nature',
  'origin', 'range', 'role', 'roots', 'shape', 'shine', 'size',
  'soul', 'strength', 'style', 'thing', 'time', 'true', 'turn',
  'unique', 'value', 'vibe', 'voice', 'warmth', 'weight', 'work',
]);

export function lintScript(text) {
  if (!text || typeof text !== 'string') return { issues: [], cleanedPreview: '' };

  const issues = [];

  // ── 1. Possessive "its" vs "it's" ──────────────────────────
  const itsRe = /\bit's\s+(\w+)/gi;
  let m;
  while ((m = itsRe.exec(text)) !== null) {
    const nextWord = m[1].toLowerCase();
    if (POSSESSIVE_NOUNS.has(nextWord)) {
      issues.push({
        type: 'grammar', severity: 'error',
        message: `"it's" should likely be "its" (possessive) before "${m[1]}"`,
        original: m[0], suggestion: m[0].replace(/it's/i, 'its'),
        offset: m.index, length: m[0].length,
      });
    }
  }

  // ── 2. Typographic apostrophes ────────────────────────────
  const typoRe = /[\u2018\u2019\u201C\u201D]/g;
  while ((m = typoRe.exec(text)) !== null) {
    const repl = m[0] === '\u2018' || m[0] === '\u2019' ? "'" : '"';
    issues.push({
      type: 'tts', severity: 'warning',
      message: 'Typographic quote — TTS may mispronounce',
      original: m[0], suggestion: repl,
      offset: m.index, length: 1,
    });
  }

  // ── 3. Plural likely missing ──────────────────────────────
  const pluralRe = /\b(more|several|many|few|various|multiple)\s+(\w+)\b/gi;
  while ((m = pluralRe.exec(text)) !== null) {
    const word = m[2];
    if (word.length > 2 && !/(?:s|es|ies|ness|ous|ics|sis|tis)$/i.test(word)
        && !/^(content|people|data|footage|music|info|stuff|equipment|fun)$/i.test(word)) {
      issues.push({
        type: 'grammar', severity: 'warning',
        message: `Plural likely missing after "${m[1]}"`,
        original: m[0], suggestion: m[0] + 's',
        offset: m.index, length: m[0].length,
      });
    }
  }

  // ── 4. URLs and domains ───────────────────────────────────
  const urlRe = /https?:\/\/[^\s)]+|www\.[^\s)]+|\b\w+\.(com|io|video|app|co|net|org)\b/gi;
  while ((m = urlRe.exec(text)) !== null) {
    issues.push({
      type: 'tts', severity: 'info',
      message: 'URL/domain will be cleaned before TTS',
      original: m[0], suggestion: m[0].replace(/\.(com|io|video|app|co|net|org)$/i, ''),
      offset: m.index, length: m[0].length,
    });
  }

  // ── 5. Long sentences ─────────────────────────────────────
  const sentences = text.split(/[.!?]+/).filter(s => s.trim());
  let sentenceOffset = 0;
  for (const sent of sentences) {
    const words = sent.trim().split(/\s+/).length;
    const idx = text.indexOf(sent.trim(), sentenceOffset);
    if (words > 40) {
      issues.push({
        type: 'style', severity: 'error',
        message: `Sentence too long (${words} words) — may sound unnatural`,
        original: sent.trim().slice(0, 60) + '…', suggestion: 'Split into shorter sentences',
        offset: idx >= 0 ? idx : 0, length: sent.trim().length,
      });
    } else if (words > 25) {
      issues.push({
        type: 'style', severity: 'warning',
        message: `Long sentence (${words} words) — may sound rushed`,
        original: sent.trim().slice(0, 60) + '…', suggestion: 'Consider splitting',
        offset: idx >= 0 ? idx : 0, length: sent.trim().length,
      });
    }
    sentenceOffset = (idx >= 0 ? idx : sentenceOffset) + sent.length;
  }

  // ── 6. Total length ───────────────────────────────────────
  if (text.length > 2500) {
    issues.push({
      type: 'tts', severity: 'error',
      message: `Text too long (${text.length} chars) — will be truncated by ElevenLabs`,
      original: '', suggestion: 'Shorten to under 2500 characters',
      offset: 2500, length: 0,
    });
  } else if (text.length > 1000) {
    issues.push({
      type: 'tts', severity: 'warning',
      message: `Approaching character limit (${text.length}/2500)`,
      original: '', suggestion: '',
      offset: 0, length: 0,
    });
  }

  // ── 7. Double spaces, space before punct ──────────────────
  const wsRe = /\s{2,}|\s+[.,!?;:]/g;
  while ((m = wsRe.exec(text)) !== null) {
    issues.push({
      type: 'style', severity: 'warning',
      message: 'Spacing issue — extra spaces or space before punctuation',
      original: m[0], suggestion: m[0].replace(/\s+([.,!?;:])/g, '$1').replace(/\s{2,}/g, ' '),
      offset: m.index, length: m[0].length,
    });
  }

  // ── 8. Markdown leftovers ─────────────────────────────────
  const mdRe = /[#@*_~`]+/g;
  while ((m = mdRe.exec(text)) !== null) {
    issues.push({
      type: 'tts', severity: 'info',
      message: 'Markdown/symbol will be removed before TTS',
      original: m[0], suggestion: '',
      offset: m.index, length: m[0].length,
    });
  }

  // ── Build cleaned preview ────────────────────────────────
  let cleaned = text;
  // Apply auto-fixable issues (sorted by offset descending to preserve positions)
  const fixable = issues
    .filter(i => i.suggestion && i.original && i.type !== 'style')
    .sort((a, b) => b.offset - a.offset);
  for (const fix of fixable) {
    const before = cleaned.slice(0, fix.offset);
    const after  = cleaned.slice(fix.offset + fix.length);
    cleaned = before + fix.suggestion + after;
  }
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  if (cleaned && !/[.!?]$/.test(cleaned)) cleaned += '.';

  return { issues, cleanedPreview: cleaned };
}

/**
 * Apply a single fix to text at the given offset.
 */
export function applyFix(text, issue) {
  if (!issue.original || issue.offset == null) return text;
  const before = text.slice(0, issue.offset);
  const after  = text.slice(issue.offset + issue.length);
  return before + issue.suggestion + after;
}

/**
 * Apply all auto-fixable issues at once.
 */
export function applyAllFixes(text, issues) {
  const fixable = issues
    .filter(i => i.suggestion && i.original)
    .sort((a, b) => b.offset - a.offset);
  let result = text;
  for (const fix of fixable) {
    const before = result.slice(0, fix.offset);
    const after  = result.slice(fix.offset + fix.length);
    result = before + fix.suggestion + after;
  }
  return result;
}
